import { useState } from "react";
import { useProgressReports } from "hooks/useProgressReports";
import ProgressSectionSkeleton from "./ProgressSectionSkeleton";
import NoProgressReports from "Assets/Img/NoProgressReports";
import { formatDate } from "utils/Common/Date";
import { itemColor } from "utils/Common/ProgressBarColor";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import Button from "Components/Common/Button/Button";
import ShareReportModal from "Components/Common/ShareReportModal/ShareReportModal";
import { useToast } from "Components/Common/Toast/ToastContext";
import { createShareLink } from "services/projectService";

interface Props {
  projectId: string;
}

const bulletLines = (text: string | null | undefined): string[] => {
  if (!text) return [];
  return text
    .split(/\n|•/)
    .map((s) => s.trim())
    .filter(Boolean);
};

export default function ProgressSection({ projectId }: Props) {
  const {
    reports,
    activeReportId,
    setActiveReportId,
    detail,
    loadingList,
    loadingDetail,
  } = useProgressReports(projectId);

  const showToast = useToast();
  const [sharing, setSharing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const [collapsedPhases, setCollapsedPhases] = useState<
    Record<string, boolean>
  >({});
  const togglePhase = (key: string) =>
    setCollapsedPhases((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleConfirmShareReport = async (emails: string[]) => {
    if (!projectId || !activeReportId) return;
    setSharing(true);
    try {
      const res: any = await createShareLink(projectId, {
        shareLinkType: "progress_report",
        role: "lender",
        resourceId: activeReportId,
        emails,
      });
      if (res?.error) {
        showToast(res.error.message || "Failed to generate share link.");
        return;
      }
      showToast("Progress report shared successfully to the lender.");
      setShowShareModal(false);
    } catch {
      showToast("Failed to generate share link.");
    } finally {
      setSharing(false);
    }
  };
  const lineItems = detail?.inspectionSnapshot?.lineItems ?? [];
  const phaseGroups = detail?.inspectionSnapshot?.phases ?? [];
  const isResidential = phaseGroups.length > 0;
  const hasAnyFlag = isResidential
    ? phaseGroups.some((p) => p.items.some((item) => item.isFlagged))
    : lineItems.some((item) => item.isFlagged);
  const photos = (detail?.documents ?? []).filter((d) => d.isIncluded);
  const scheduleLines = bulletLines(detail?.scheduleNotes);
  const changeOrderLines = bulletLines(detail?.changeOrderNotes);
  const bankActionLines = bulletLines(detail?.bankActionItems);
  const reporterName = detail
    ? `${detail.reportBy?.firstName} ${detail.reportBy?.lastName}`.trim()
    : "";

  return (
    <div>
      <h1 className="sectionTitle">Construction Progress Reports</h1>
      <div className="section-sub">
        Field inspection reports per draw — phases, line items, photos, and
        field notes
      </div>

      {loadingList ? (
        <ProgressSectionSkeleton />
      ) : !reports.length ? (
        <EmptyState
          icon={<NoProgressReports />}
          title="No Progress Reports Yet"
          subtitle="Progress reports will appear here once inspections are completed and submitted."
        />
      ) : (
        <>
          <div className="d-flex align-items-center justify-content-between">
            <div className="report-tabs">
              {reports.map((r) => (
                <Button
                  key={r.id}
                  className={`report-tab${activeReportId === r.id ? " active" : ""}`}
                  onClick={() => setActiveReportId(r.id)}
                >
                  Report {r.reportNumber} — {formatDate(r.reportDate, false)}
                </Button>
              ))}
            </div>
            <div className="report-tabs">
              <Button
                className="report-tab active"
                onClick={() => setShowShareModal(true)}
                title="Share progress report to lender"
              >
                {`Share Report ${activeReportId ? `#${String(reports.find((r) => r.id === activeReportId)?.reportNumber ?? "").padStart(2, "0")}` : ""}`}
              </Button>
            </div>
          </div>
          {loadingDetail ? (
            <ProgressSectionSkeleton />
          ) : detail ? (
            <>
              <div className="card">
                <div className="card-title">
                  Report {detail.reportNumber} Overview
                  <span className="card-subtitle">
                    {detail.inspectionDate && (
                      <>
                        Inspection: {formatDate(detail.inspectionDate, false)}{" "}
                        ·{" "}
                      </>
                    )}
                    Report Date: {formatDate(detail.reportDate, false)}
                    {detail.inspectorName && (
                      <> · Inspector: {detail.inspectorName}</>
                    )}
                  </span>
                </div>
                <div className="three-col">
                  <div className="metric">
                    <div className="metric-label">CSC Estimate</div>
                    <div className="metric-val">
                      {detail.cscEstimation != null
                        ? `${parseFloat(detail.cscEstimation)}%`
                        : "—"}
                    </div>
                    <div className="metric-sub">Overall completion</div>
                  </div>
                  <div className={`metric${hasAnyFlag ? " flagged" : ""}`}>
                    <div className="metric-label">GC Billed</div>
                    <div
                      className={`metric-val${hasAnyFlag ? " red" : " amber"}`}
                    >
                      {detail.drawRequest?.gcBilledPct != null
                        ? `${detail.drawRequest.gcBilledPct}%`
                        : "—"}
                      {hasAnyFlag && " ⚠"}
                    </div>
                    <div className="metric-sub">
                      Application #{detail.reportNumber}
                    </div>
                  </div>
                  <div className="metric">
                    <div className="metric-label">Draw Amount</div>
                    <div className="metric-val">
                      {detail.drawRequest?.requestedAmount?.amount != null
                        ? `$${detail.drawRequest.requestedAmount.amount.toLocaleString()}`
                        : "—"}
                    </div>
                    <div className="metric-sub">Current payment due</div>
                  </div>
                </div>
                {isResidential ? (
                  <>
                    <div className="progress-phase">Progress by Phase</div>
                    {phaseGroups.map((phase: any) => {
                      const isCollapsed = Boolean(collapsedPhases[phase.phase]);
                      const phaseColor = itemColor(phase.phaseCscEstimation);
                      return (
                        <div className="rpt-phase-section" key={phase.phase}>
                          <div
                            className={`rpt-phase-header${isCollapsed ? " collapsed" : ""}`}
                            onClick={() => togglePhase(phase.phase)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                togglePhase(phase.phase);
                            }}
                          >
                            <div className="rpt-ph-left">
                              <span className="rpt-ph-label">
                                Phase {phase.phase}
                              </span>
                              <span className="rpt-ph-weight">
                                {phase.phaseWeight}% of total
                              </span>
                            </div>
                            <div className="rpt-ph-right">
                              <span
                                className="rpt-ph-pct"
                                style={{ color: phaseColor }}
                              >
                                {phase.phaseCscEstimation.toFixed(1)}%
                              </span>
                              <span className="rpt-ph-chevron">▼</span>
                            </div>
                          </div>
                          <div
                            className={`rpt-phase-body${isCollapsed ? " collapsed" : ""}`}
                          >
                            {phase.items.map((item: any) => {
                              const pct = item.cscEstimation ?? 0;
                              const color = itemColor(pct, item.isFlagged);
                              const noteEntries = item.note
                                ? Object.entries(item.note)
                                    .filter(
                                      ([k]) =>
                                        k !== "flag" && !isNaN(Number(k)),
                                    )
                                    .map(([, v]) => v as string)
                                : [];
                              return (
                                <div
                                  className={`rpt-fi-item${item.isFlagged ? " flagged" : ""}`}
                                  key={item.id}
                                >
                                  <div className="rpt-fi-top">
                                    <span className="rpt-fi-code">
                                      {item.itemCode}
                                    </span>
                                    <span className="rpt-fi-label">
                                      {item.itemLabel ?? "—"}
                                    </span>
                                    <span
                                      className="rpt-fi-pct"
                                      style={{ color }}
                                    >
                                      {pct}%
                                      {item.isFlagged && (
                                        <span className="rpt-fi-flag">⚠</span>
                                      )}
                                    </span>
                                  </div>
                                  <div className="phase-bar-track">
                                    <div
                                      className="phase-bar-fill"
                                      style={{
                                        width: `${pct}%`,
                                        background: color,
                                      }}
                                    />
                                  </div>
                                  {noteEntries.length > 0 && (
                                    <ul className="phase-notes">
                                      {noteEntries.map((note, i) => (
                                        <li key={i}>{note}</li>
                                      ))}
                                    </ul>
                                  )}
                                  {item.isFlagged && item.note?.flag && (
                                    <p className="flag-note">
                                      {item.note.flag}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  lineItems.map((item: any) => {
                    const pct = item.cscEstimation ?? 0;
                    const billed = item.gcBilledPct ?? 0;
                    const color = itemColor(pct, item.isFlagged);
                    const noteEntries = item.note
                      ? Object.entries(item.note)
                          .filter(([k]) => k !== "flag" && !isNaN(Number(k)))
                          .map(([, v]) => v as string)
                      : [];
                    return (
                      <div className="phase-block" key={item.id}>
                        <div className="phase-header">
                          <span className="phase-name">
                            {item.aiaItem?.itemName ?? "—"}
                          </span>
                          <span className="phase-pct" style={{ color }}>
                            {pct}% (billed {billed}%)
                            {item.isFlagged && (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                style={{
                                  marginLeft: 5,
                                  verticalAlign: "middle",
                                }}
                              >
                                <path
                                  d="M6 1L11 10H1L6 1Z"
                                  stroke="currentColor"
                                  strokeWidth="1.2"
                                  strokeLinejoin="round"
                                  fill="none"
                                />
                                <line
                                  x1="6"
                                  y1="5"
                                  x2="6"
                                  y2="7.5"
                                  stroke="currentColor"
                                  strokeWidth="1.2"
                                  strokeLinecap="round"
                                />
                                <circle
                                  cx="6"
                                  cy="9"
                                  r=".5"
                                  fill="currentColor"
                                />
                              </svg>
                            )}
                          </span>
                        </div>
                        <div className="phase-bar-track">
                          <div
                            className="phase-bar-fill"
                            style={{ width: `${pct}%`, background: color }}
                          />
                        </div>
                        {noteEntries.length > 0 && (
                          <ul className="phase-notes">
                            {noteEntries.map((note, i) => (
                              <li key={i}>{note}</li>
                            ))}
                          </ul>
                        )}
                        {item.isFlagged && item.note?.flag && (
                          <p className="flag-note">{item.note.flag}</p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {photos.length > 0 && (
                <div className="card">
                  <div className="card-title">
                    Progress Photos — {formatDate(detail.reportDate, false)}{" "}
                    Inspection
                  </div>
                  <div className="photos-grid">
                    {photos.map((p: any, i: number) => (
                      <div className="photo-card" key={p.id}>
                        {p.document?.url ? (
                          <img
                            src={p.document.url}
                            alt={p.description ?? p.note}
                            className="photo-img"
                          />
                        ) : (
                          <div className="photo-placeholder">
                            <div className="photo-num">{i + 1}</div>
                          </div>
                        )}
                        {(p.description ?? p.note) && (
                          <div className="photo-caption">
                            {p.description ?? p.note}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="card closingNotesSec">
                <div className="card-title">
                  Closing Notes &amp; Bank Actions
                </div>
                <div className="two-col">
                  <div>
                    <div className="ddTitle">Items for Bank to Verify</div>
                    {bankActionLines.length > 0 ? (
                      <ul className="note-list">
                        {bankActionLines.map((n: any, i: number) => (
                          <li key={i}>{n}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="noDataMessage">No bank action items.</p>
                    )}
                  </div>
                  <div>
                    {scheduleLines.length > 0 && (
                      <>
                        <div className="ddTitle">
                          Schedule &amp; Change Orders
                        </div>
                        <ul className="note-list">
                          {scheduleLines.map((n: any, i: number) => (
                            <li key={i}>{n}</li>
                          ))}
                          {changeOrderLines.map((n: any, i: number) => (
                            <li key={`co-${i}`}>{n}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {detail.recommendationText && (
                      <div className="actionNote">
                        <strong>Action Required:</strong>{" "}
                        {detail.recommendationText}
                      </div>
                    )}
                  </div>
                </div>
                <div className="reportNote">
                  {reporterName && (
                    <>
                      Report by: <strong>{reporterName}</strong>&nbsp;·&nbsp;
                    </>
                  )}
                  Report Date: {formatDate(detail.reportDate, false)}
                  {detail.invoiceAmount?.amount != null && (
                    <>
                      &nbsp;·&nbsp; CSC Invoice: $
                      {detail.invoiceAmount.amount.toLocaleString()}
                    </>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </>
      )}

      <ShareReportModal
        show={showShareModal}
        title="Share Progress Report"
        loading={sharing}
        onClose={() => setShowShareModal(false)}
        onConfirm={handleConfirmShareReport}
      />
    </div>
  );
}
